import { useState } from "react";
import { motion } from "framer-motion";
import { MagnifyingGlass, ArrowUpRight, CheckCircle, XCircle, Globe } from "@phosphor-icons/react";
import { PageHeader } from "../components/PageHeader";
import { FadeUp } from "../components/Reveal";
import { api } from "../lib/api";

export default function Domains() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");

  const search = async (e) => {
    e.preventDefault();
    const clean = query.trim().toLowerCase();
    if (!clean) return;
    setSearching(true);
    setError("");
    setResults(null);
    try {
      const { data } = await api.get(`/domains/check?name=${encodeURIComponent(clean)}`);
      setResults(data.results);
    } catch (err) {
      setError(err.response?.data?.detail || "Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <>
      <PageHeader
        testId="domains-page"
        overline="Domains & Hosting"
        title={["Find your name.", "Claim your domain."]}
        subtitle="Search across a dozen extensions and register in minutes. AI-native hosting and healthcare-grade builds available from the Anxya Tech team once you've secured the name."
      />
      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <FadeUp>
            <form onSubmit={search} data-testid="domain-search-form" className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Globe size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-primary/40" />
                <input
                  data-testid="domain-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="yourbrandname"
                  className="h-14 w-full rounded-full border border-primary/15 bg-white pl-12 pr-4 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                />
              </div>
              <button
                type="submit"
                data-testid="domain-search-btn"
                disabled={searching}
                className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-primary px-8 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
              >
                <MagnifyingGlass size={18} weight="bold" />
                {searching ? "Searching…" : "Search Domains"}
              </button>
            </form>
            {error && <p data-testid="domain-search-error" className="mt-4 text-sm text-red-600">{error}</p>}
          </FadeUp>

          {searching && (
            <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-24 animate-pulse rounded-2xl border border-primary/10 bg-white" />
              ))}
            </div>
          )}

          {results && (
            <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="domain-results">
              {results.map((r, i) => (
                <motion.div
                  key={r.domain}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  data-testid={`domain-result-${r.domain.replace(/\./g, "-")}`}
                  className={`rounded-2xl border p-5 ${r.available ? "border-accent/40 bg-white" : "border-primary/10 bg-muted/40"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-display text-lg font-medium tracking-tight text-primary">{r.domain}</p>
                    {r.available === true && <CheckCircle size={20} weight="fill" className="text-accent shrink-0" />}
                    {r.available === false && <XCircle size={20} weight="fill" className="text-primary/25 shrink-0" />}
                  </div>
                  <p className={`mt-1 text-xs font-semibold ${r.available ? "text-accent" : "text-muted-foreground"}`}>
                    {r.available === true ? "Available" : r.available === false ? "Taken" : "Unknown"}
                  </p>
                  {r.available && (
                    <a
                      href={`https://www.hostinger.com/domain-name-search?domain=${r.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`domain-buy-${r.domain.replace(/\./g, "-")}`}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
                    >
                      Buy on Hostinger <ArrowUpRight size={12} weight="bold" />
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          )}

          <FadeUp delay={0.2}>
            <div className="mt-16 rounded-3xl bg-primary p-8 lg:p-12 text-white">
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-accent">Beyond the name</p>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl font-medium tracking-tight">
                Domain secured? We'll build what goes on it.
              </h2>
              <p className="mt-3 max-w-xl text-sm text-white/60 leading-relaxed">
                AI-native websites, products, and platforms — designed, built, and hosted by Anxya Tech in days, not months.
              </p>
              <a
                href="/demo"
                data-testid="domains-talk-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-semibold text-primary transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30"
              >
                Talk to Us <ArrowUpRight size={16} weight="bold" />
              </a>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
